import React, { useState } from 'react';
import { Card, Button, Space, Typography, message, Modal, InputNumber, List, Tag, Tooltip, Divider, Row, Col, Alert } from 'antd';
import { CopyOutlined, LinkOutlined, InfoCircleOutlined, ArrowRightOutlined, CheckCircleOutlined, ShareAltOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import auctionService from '../../services/auctionService';
import { useSelector } from 'react-redux';
import env from '../../env';

const { Text, Title } = Typography;

const DirectLinkManager = ({ auction, visible, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [directLinks, setDirectLinks] = useState([]);
    const [linkCount, setLinkCount] = useState(1);
    const [showInstructionModal, setShowInstructionModal] = useState(false);
    const { currentUser } = useSelector((state) => state.user);
    const isOwner = currentUser?.id === auction?.user_id;
    const navigate = useNavigate();

    const generateDirectLinks = async () => {
        try {
            setLoading(true);
            console.log('Generating links, count:', linkCount);
            const response = await auctionService.generateDirectLink(auction.slug, linkCount);
            console.log('Response received:', response);
            
            // Handle both single link and multiple links response
            let links = [];
            if (Array.isArray(response.data)) {
                // Multiple links returned
                console.log('Multiple links detected:', response.data.length);
                links = response.data.map(item => 
                    item.direct_link.replace(window.location.origin, env.FRONTEND_URL)
                );
            } else {
                // Single link returned (backward compatibility)
                console.log('Single link detected');
                links = [response.data.direct_link.replace(window.location.origin, env.FRONTEND_URL)];
            }
            
            console.log('Final links array:', links);
            setDirectLinks(links);
            message.success(`${linkCount} direct link(s) generated successfully`);
            // Show instruction modal after successful generation
            setShowInstructionModal(true);
        } catch (error) {
            console.error('Error generating links:', error);
            message.error(error.response?.data?.message || 'Failed to generate direct links');
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (link) => {
        navigator.clipboard.writeText(link);
        message.success('Link copied to clipboard');
    };

    return (
        <Modal
            title={null}
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width="90%"
            style={{ maxWidth: '1000px', padding: 0 }}
            closable={true}
            maskClosable={true}
            className="direct-link-manager-modal"
        >
            <div style={{ 
                background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                borderRadius: '12px',
                overflow: 'hidden'
            }}>
                {/* Header Section */}
                <div className="direct-link-modal-header" style={{
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    padding: '20px 24px 16px 24px',
                    textAlign: 'center',
                    borderBottom: 'none'
                }}>
                    <div style={{
                        width: '48px',
                        height: '48px',
                        margin: '0 auto 12px',
                        background: 'rgba(255, 255, 255, 0.2)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                    }}>
                        <LinkOutlined style={{ fontSize: '24px', color: 'white' }} />
                    </div>
                    <Title level={4} style={{ 
                        color: 'white', 
                        margin: 0,
                        fontWeight: 600,
                        fontSize: '20px'
                    }}>
                        Direct Invitation Links
                    </Title>
                    <p style={{ 
                        color: 'rgba(255, 255, 255, 0.9)', 
                        fontSize: '13px',
                        display: 'block',
                        marginTop: '6px',
                        margin: 0
                    }}>
                        Generate secure, one-time use invitation links for your private auction
                    </p>
                </div>

                {/* Content Section - Side by Side */}
                <div className="direct-link-modal-content" style={{ padding: '24px' }}>
                    <Row gutter={[20, 20]}>
                        {/* Left Column - Generate Links Form */}
                        <Col xs={24} md={12}>
                <Card 
                    className="form-section"
                    style={{ 
                        borderRadius: '12px',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        border: '1px solid #e8f4f8',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                    bodyStyle={{ 
                        padding: '20px',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%'
                    }}
                >
                    <div style={{ 
                        borderBottom: '2px solid #f0f7ff',
                        paddingBottom: 12,
                        marginBottom: 20,
                        background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                        margin: '-20px -20px 20px -20px',
                        padding: '16px 20px'
                    }}>
                        <Space align="center" size="small">
                            <div style={{
                                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                padding: '8px',
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                            }}>
                                <LinkOutlined style={{ color: 'white', fontSize: '18px' }} />
                            </div>
                            <div>
                                <Title level={5} style={{ margin: 0, color: '#1a1a1a', fontWeight: 600 }}>
                                    Generate New Links
                                </Title>
                                <small style={{ fontSize: '13px', color: '#595959', display: 'block', marginTop: 2 }}>
                                    Select how many secure links you want to generate
                                </small>
                            </div>
                        </Space>
                    </div>

                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Alert
                            message={<span style={{ fontSize: '13px', fontWeight: 600 }}>Secure One-Time Links</span>}
                            description={
                                <div style={{ fontSize: '12px', marginTop: 4 }}>
                                    <p style={{ margin: 0, marginBottom: 8 }}>
                                    Each generated link can only be used once. Share these links securely with your participants.
                                    </p>
                                    <div style={{ 
                                        padding: '4px 12px', 
                                        background: '#f0f8ff', 
                                        borderRadius: '6px',
                                        border: '1px solid #d6e4ff',
                                        marginTop: 8
                                    }}>
                                        <small style={{ fontSize: '11px', color: '#595959', display: 'block', marginBottom: 4 }}>
                                            Previous generated links can be found on{' '}
                                            <Button 
                                                type="link" 
                                                size="small"
                                                style={{ 
                                                    padding: 0, 
                                                    height: 'auto', 
                                                    fontSize: '11px',
                                                    fontWeight: 600,
                                                    color: '#1890ff'
                                                }}
                                                onClick={() => {
                                                    onClose();
                                                    navigate('/invitations/sent?tab=direct');
                                                }}
                                            >
                                                 Direct Links
                                            </Button>
                                        </small>
                                    </div>
                                </div>
                            }
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined />}
                            style={{ 
                                borderRadius: '8px',
                                border: '1px solid #91d5ff',
                                marginBottom: '16px'
                            }}
                        />

                        <div style={{ textAlign: 'center', padding: '8px 0' }}>
                            <Space align="baseline" style={{ width: '100%', justifyContent: 'center' }}>
                                <span style={{ fontSize: '14px', fontWeight: 600 }}>Number of links:</span>
                                <InputNumber
                                    min={1}
                                    max={10}
                                    value={linkCount}
                                    onChange={setLinkCount}
                                    style={{ width: 80, borderRadius: '8px' }}
                                    size="large"
                                />
                            </Space>
                        </div>

                        <div style={{ textAlign: 'center', marginTop: '8px' }}>
                            <Button
                                type="primary"
                                icon={<LinkOutlined />}
                                onClick={generateDirectLinks}
                                loading={loading}
                                size="large"
                                style={{
                                    minWidth: 180,
                                    height: '40px',
                                    borderRadius: '8px',
                                    fontWeight: 500,
                                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                    border: 'none',
                                    boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                }}
                            >
                                {loading ? 'Generating...' : 'Generate Links'}
                            </Button>
                        </div>
                    </Space>
                </Card>
                        </Col>

                        {/* Right Column - Generated Links List */}
                        <Col xs={24} md={12}>
                            <Card 
                                style={{ 
                                    borderRadius: '12px',
                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                    border: '1px solid #e8f4f8',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}
                                bodyStyle={{ 
                                    padding: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%'
                                }}
                            >
                                <div style={{ 
                                    borderBottom: '2px solid #f0f7ff',
                                    paddingBottom: 12,
                                    marginBottom: 20,
                                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                                    margin: '-20px -20px 20px -20px',
                                    padding: '16px 20px'
                                }}>
                                    <Space align="center" size="small">
                                        <div style={{
                                            background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                                            padding: '8px',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                        }}>
                                            <LinkOutlined style={{ color: 'white', fontSize: '18px' }} />
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <p className="generated-links-title" style={{ margin: 0, color: '#1a1a1a', fontWeight: 600, fontSize: '16px' }}>
                                                Generated Links
                                            </p>
                                            <small style={{ fontSize: '12px', color: '#595959', display: 'block', marginTop: 2 }}>
                                                {directLinks.length} {directLinks.length === 1 ? 'link' : 'links'} generated
                                            </small>
                                        </div>
                                    </Space>
                                </div>

                                <div style={{ flex: 1, overflowY: 'auto', maxHeight: '500px' }}>
                                    {directLinks.length === 0 ? (
                                        <Card 
                                            bordered={false}
                                            style={{ 
                                                background: '#fafafa',
                                                borderRadius: '8px',
                                                textAlign: 'center',
                                                padding: '24px 16px',
                                                border: '1px solid #e8e8e8'
                                            }}
                                        >
                                            <LinkOutlined style={{ fontSize: '36px', color: '#bfbfbf', marginBottom: '12px' }} />
                                            <p style={{ fontSize: '14px', display: 'block', color: '#8c8c8c', margin: 0 }}>
                                                No links generated yet
                                            </p>
                                            <small style={{ fontSize: '12px', display: 'block', marginTop: 4, color: '#8c8c8c' }}>
                                                Generate links using the form above
                                            </small>
                                        </Card>
                                    ) : (
                                        <List
                                            dataSource={directLinks}
                                            renderItem={(link, index) => (
                                                <List.Item
                                                    style={{
                                                        background: '#fff',
                                                        marginBottom: 8,
                                                        padding: '12px 16px',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e8f4f8',
                                                        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.04)',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                    actions={[
                                                        <Tooltip title="Copy link">
                                                            <Button
                                                                key="copy"
                                                                icon={<CopyOutlined />}
                                                                onClick={() => copyToClipboard(link)}
                                                                type="text"
                                                                size="middle"
                                                                style={{ borderRadius: '6px' }}
                                                            >
                                                                Copy
                                                            </Button>
                                                        </Tooltip>
                                                    ]}
                                                >
                                                    <List.Item.Meta
                                                        title={
                                                            <Space wrap>
                                                                <span style={{ fontSize: '14px', fontWeight: 600 }}>Link {index + 1}</span>
                                                                <Tag color="blue" style={{ borderRadius: '6px', fontSize: '11px', fontWeight: 500 }}>
                                                                    One-time use
                                                                </Tag>
                                                            </Space>
                                                        }
                                                        description={
                                                            <small 
                                                                className="direct-link-url"
                                                                style={{ 
                                                                    fontSize: '12px',
                                                                    fontFamily: 'monospace',
                                                                    wordBreak: 'break-all',
                                                                    marginTop: 4,
                                                                    color: '#8c8c8c',
                                                                    display: 'block'
                                                                }}
                                                            >
                                                                {link}
                                                            </small>
                                                        }
                                                    />
                                                </List.Item>
                                            )}
                                        />
                                    )}
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>
            <style>{`
                .direct-link-manager-modal .ant-modal-content {
                    border-radius: 12px;
                    overflow: hidden;
                    box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                }
                
                .direct-link-manager-modal .ant-modal-close {
                    display: flex;
                    font-size: 16px;
                    font-style: normal;
                    line-height: 32px;
                    justify-content: center;
                    text-transform: none;
                    text-rendering: auto;
                    color: #ffffff;
                    padding: 10px;
                    background: #333333;
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                    top: 12px;
                    right: 12px;
                }
                
                .direct-link-manager-modal .ant-modal-close:hover {
                    color: white;
                }
                
                @media (max-width: 991px) {
                    .direct-link-manager-modal .ant-modal {
                        width: 95% !important;
                        max-width: 95% !important;
                        margin: 10px auto !important;
                    }
                    
                    .direct-link-modal-header {
                        padding: 16px 20px 12px 20px !important;
                    }
                    
                    .direct-link-modal-header h4.ant-typography {
                        font-size: 18px !important;
                    }
                    
                    .direct-link-modal-header p {
                        font-size: 12px !important;
                    }
                    
                    .direct-link-modal-content {
                        padding: 20px 16px !important;
                    }
                    
                    .form-section .ant-card-body {
                        padding: 16px !important;
                    }
                    
                    .form-section h5.ant-typography {
                        font-size: 16px !important;
                    }
                    
                    .generated-links-title {
                        font-size: 15px !important;
                    }
                    
                    .ant-row {
                        margin-left: 0 !important;
                        margin-right: 0 !important;
                    }
                    
                    .ant-col {
                        padding-left: 0 !important;
                        padding-right: 0 !important;
                    }
                }
                
                @media (max-width: 767px) {
                    .direct-link-manager-modal .ant-modal {
                        width: 98% !important;
                        max-width: 98% !important;
                    }
                    
                    .direct-link-modal-header {
                        padding: 12px 16px 10px 16px !important;
                    }
                    
                    .direct-link-modal-header h4.ant-typography {
                        font-size: 16px !important;
                    }
                    
                    .direct-link-modal-content {
                        padding: 16px 12px !important;
                    }
                    
                    .form-section .ant-card-body {
                        padding: 12px !important;
                    }
                    
                    .form-section h5.ant-typography {
                        font-size: 14px !important;
                    }
                    
                    .generated-links-title {
                        font-size: 14px !important;
                    }
                    
                    .ant-btn {
                        width: 100% !important;
                        min-width: auto !important;
                    }
                    
                    .ant-list-item {
                        flex-direction: column !important;
                        align-items: flex-start !important;
                    }
                    
                    .ant-list-item-action {
                        margin-left: 0 !important;
                        margin-top: 8px !important;
                        width: 100% !important;
                    }
                    
                    .ant-list-item-action li {
                        width: 100% !important;
                    }
                    
                    .ant-list-item-action .ant-btn {
                        width: 100% !important;
                        justify-content: flex-start !important;
                        text-align: left !important;
                    }
                    
                    .direct-link-url {
                        display: none !important;
                    }
                    
                }
                
                @media (max-width: 480px) {
                    .direct-link-modal-header h4.ant-typography {
                        font-size: 14px !important;
                    }
                    
                    .direct-link-modal-header p {
                        font-size: 11px !important;
                    }
                    
                    .form-section h5.ant-typography {
                        font-size: 13px !important;
                    }
                    
                    .generated-links-title {
                        font-size: 13px !important;
                    }
                }
            `}</style>

            {/* Instruction Modal */}
            <Modal
                title={null}
                open={showInstructionModal}
                onCancel={() => setShowInstructionModal(false)}
                footer={null}
                centered
                width="90%"
                style={{ maxWidth: '600px', padding: 0 }}
                closable={true}
                maskClosable={true}
                className="direct-link-instruction-modal"
            >
                <div style={{ 
                    background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%)',
                    borderRadius: '12px',
                    overflow: 'hidden'
                }}>
                    {/* Header Section */}
                    <div className="instruction-modal-header" style={{
                        background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                        padding: '20px 24px 16px 24px',
                        textAlign: 'center',
                        borderBottom: 'none'
                    }}>
                        <div style={{
                            width: '48px',
                            height: '48px',
                            margin: '0 auto 12px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(10px)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                        }}>
                            <CheckCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
                        </div>
                        <Title level={4} style={{ 
                            color: 'white', 
                            margin: 0,
                            fontWeight: 600,
                            fontSize: '20px'
                        }}>
                            Links Generated Successfully!
                        </Title>
                        <p style={{ 
                            color: 'rgba(255, 255, 255, 0.9)', 
                            fontSize: '13px',
                            display: 'block',
                            marginTop: '6px',
                            margin: 0
                        }}>
                            {linkCount} {linkCount === 1 ? 'link' : 'links'} ready to share
                        </p>
                    </div>

                    {/* Content Section */}
                    <div className="instruction-modal-content" style={{ padding: '24px' }}>
                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                            {/* How to Share Section */}
                            <div>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    marginBottom: '12px'
                                }}>
                                    <div style={{
                                        background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                                        padding: '8px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <ShareAltOutlined style={{ color: 'white', fontSize: '18px' }} />
                                    </div>
                                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                                        How to Share These Links
                                    </span>
                                </div>
                                <ul style={{ 
                                    margin: 0, 
                                    paddingLeft: '20px',
                                    fontSize: '14px',
                                    color: '#595959',
                                    lineHeight: '1.8'
                                }}>
                                    <li>Copy each link using the "Copy" button next to it</li>
                                    <li>Send or forward the links to your participants via email, messaging apps, or any communication method</li>
                                    <li>Each link can only be used once - once someone uses it to join, it becomes inactive</li>
                                    <li>Share links securely and only with intended participants</li>
                                </ul>
                            </div>

                            <Divider style={{ margin: '8px 0', borderColor: '#e8f4f8' }} />

                            {/* Where to Find Later Section */}
                            <div>
                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: '12px',
                                    marginBottom: '12px'
                                }}>
                                    <div style={{
                                        background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                                        padding: '8px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        <LinkOutlined style={{ color: 'white', fontSize: '18px' }} />
                                    </div>
                                    <span style={{ fontSize: '16px', fontWeight: 600, color: '#262626' }}>
                                        Where to Find These Links Later
                                    </span>
                                </div>
                                <div style={{ 
                                    padding: '12px 16px',
                                    background: '#f0f8ff',
                                    borderRadius: '8px',
                                    border: '1px solid #d6e4ff',
                                    marginBottom: '12px'
                                }}>
                                    <p style={{ 
                                        fontSize: '14px', 
                                        color: '#595959', 
                                        margin: 0,
                                        marginBottom: '8px',
                                        lineHeight: '1.6'
                                    }}>
                                        All your generated direct links are saved and can be accessed anytime from:
                                    </p>
                                    <Button 
                                        type="link" 
                                        size="small"
                                        icon={<ArrowRightOutlined />}
                                        style={{ 
                                            padding: 0, 
                                            height: 'auto', 
                                            fontSize: '14px',
                                            fontWeight: 600,
                                            color: '#1890ff'
                                        }}
                                        onClick={() => {
                                            setShowInstructionModal(false);
                                            onClose();
                                            navigate('/invitations/sent?tab=direct');
                                        }}
                                    >
                                        Sent Invitations → Direct Links
                                    </Button>
                                </div>
                            </div>

                            {/* Footer Actions */}
                            <div style={{ 
                                marginTop: '16px',
                                paddingTop: '16px',
                                borderTop: '1px solid #f0f0f0',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: '12px'
                            }}>
                                <Button 
                                    onClick={() => setShowInstructionModal(false)}
                                    size="large"
                                    style={{
                                        borderRadius: '8px',
                                        fontWeight: 500,
                                        padding: '0 24px',
                                        height: '40px'
                                    }}
                                >
                                    Got it!
                                </Button>
                            </div>
                        </Space>
                    </div>
                </div>
                <style>{`
                    .direct-link-instruction-modal .ant-modal-content {
                        border-radius: 12px;
                        overflow: hidden;
                        box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
                    }
                    
                    .direct-link-instruction-modal .ant-modal-close {
                        display: flex;
                        font-size: 16px;
                        font-style: normal;
                        line-height: 32px;
                        justify-content: center;
                        text-transform: none;
                        text-rendering: auto;
                        color: #ffffff;
                        padding: 10px;
                        background: #333333;
                        border-radius: 50%;
                        width: 30px;
                        height: 30px;
                        top: 12px;
                        right: 12px;
                    }
                    
                    .direct-link-instruction-modal .ant-modal-close:hover {
                        color: white;
                    }
                    
                    @media (max-width: 767px) {
                        .direct-link-instruction-modal .ant-modal {
                            width: 95% !important;
                            max-width: 95% !important;
                            margin: 10px auto !important;
                        }
                        
                        .instruction-modal-header {
                            padding: 16px 20px 12px 20px !important;
                        }
                        
                        .instruction-modal-header h4.ant-typography {
                            font-size: 18px !important;
                        }
                        
                        .instruction-modal-header p {
                            font-size: 12px !important;
                        }
                        
                        .instruction-modal-content {
                            padding: 20px 16px !important;
                        }
                        
                        .instruction-modal-content ul {
                            font-size: 13px !important;
                        }
                        
                        .instruction-modal-content > div > div:last-child {
                            flex-direction: column !important;
                        }
                        
                        .instruction-modal-content > div > div:last-child .ant-btn {
                            width: 100% !important;
                        }
                    }
                    
                    @media (max-width: 480px) {
                        .instruction-modal-header {
                            padding: 12px 16px 10px 16px !important;
                        }
                        
                        .instruction-modal-header h4.ant-typography {
                            font-size: 16px !important;
                        }
                        
                        .instruction-modal-content {
                            padding: 16px 12px !important;
                        }
                        
                        .instruction-modal-content ul {
                            font-size: 12px !important;
                        }
                    }
                `}</style>
            </Modal>
        </Modal>
    );
};

export default DirectLinkManager; 